import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  NativeSelect,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgs } from '../api/orgs';
import {
  useClinicalPatients,
  usePlatformUsers,
  usePromoteClinicalPatient,
  useRolesForOrg,
  type ClinicalPatientRow,
  type ListFilters,
} from '../api/platform-users';
import { useProducts } from '../api/products';
import { SearchLoading } from '../components/search-loading';

function userTypeLabel(t: string): string {
  if (t === 'clinician') return 'Practitioner';
  if (t === 'patient') return 'Patient';
  if (t === 'admin') return 'Org admin';
  if (t === 'user') return 'User';
  if (t === 'documentation') return 'Documentation';
  return t;
}

export function PlatformUsersPage() {
  const [params, setParams] = useSearchParams();
  const { data: orgs } = useOrgs();

  const org = params.get('org') ?? '';
  const type = (params.get('type') as ListFilters['type']) ?? undefined;
  const status = (params.get('status') as ListFilters['status']) ?? undefined;
  const searchParam = params.get('search') ?? '';
  const productId = params.get('product') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  // Products list drives the filter dropdown. Same data as the
  // platform-wide products page — admin-only platform products,
  // active or retired, soft-deleted excluded.
  const productsQuery = useProducts({ includeDeleted: false });

  const [searchInput, setSearchInput] = useState(searchParam);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  // Sync the debounced search back to URL params (without nuking other params).
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    if (debouncedSearch !== searchParam) {
      next.set('page', '1');
      setParams(next, { replace: true });
    }
  }, [debouncedSearch]);

  const filters: ListFilters = {
    org: org || undefined,
    type,
    status,
    search: debouncedSearch || undefined,
    productId: productId || undefined,
    page,
  };
  const query = usePlatformUsers(filters);

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  // Clinical patients (from clinical-api) interleave with platform
  // users on this page. Fetched on the same page index as the
  // platform-users list — pagination is independent because the two
  // sources have different totals, and we show them as separate table
  // sections to keep the shapes distinct.
  const clinicalQuery = useClinicalPatients(
    org ? { org, search: debouncedSearch || undefined, page } : null,
  );

  // De-dupe clinical patients that already have a platform-user row
  // (the user has been promoted previously). Platform-user records
  // include `patient.clinical_patient_id` when the link exists.
  const promotedClinicalIds = useMemo(() => {
    const set = new Set<string>();
    for (const u of query.data?.items ?? []) {
      const cid = u.patient?.clinical_patient_id;
      if (cid) set.add(cid);
    }
    return set;
  }, [query.data]);

  // Server-side search is global now; only de-dupe patients already promoted
  // to a platform-user row (those appear in the platform-users table above).
  const clinicalOnly: ClinicalPatientRow[] = (clinicalQuery.data?.items ?? []).filter(
    (p) => !promotedClinicalIds.has(p.id),
  );

  // Promote-modal state. `promoting` carries the row whose Promote
  // button was clicked; closing nulls it out. The mutation hook is
  // top-level (one per page render) and operates on the current
  // `promoting` payload at submit time.
  const [promoting, setPromoting] = useState<ClinicalPatientRow | null>(null);
  const [promoteOpen, { open: openPromote, close: closePromote }] = useDisclosure(false);

  function patchParams(patch: Partial<Record<string, string>>, resetPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.set('page', '1');
    setParams(next, { replace: true });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Users</Title>
        <Group gap="sm">
          <Button component={Link} to="/users/duplicates" variant="default">
            Review duplicates
          </Button>
          <Button component={Link} to="/users/new" leftSection={<IconPlus size={16} />}>
            Create user
          </Button>
        </Group>
      </Group>

      <Group gap="md" align="flex-end" wrap="wrap">
        <Select
          label="Organisation"
          placeholder="All organisations"
          value={org || null}
          onChange={(v) => patchParams({ org: v ?? '' }, true)}
          data={(orgs ?? []).map((o) => ({ value: o.id, label: o.name }))}
          searchable
          clearable
          w={260}
        />
        <Select
          label="Type"
          value={type ?? null}
          onChange={(v) => patchParams({ type: v ?? '' }, true)}
          data={[
            { value: '', label: 'All' },
            { value: 'clinician', label: 'Practitioner' },
            { value: 'patient', label: 'Patient' },
            { value: 'user', label: 'User' },
            { value: 'documentation', label: 'Documentation' },
          ]}
          w={160}
        />
        <Select
          label="Status"
          value={status ?? 'active'}
          onChange={(v) => patchParams({ status: v ?? '' }, true)}
          data={[
            { value: 'active', label: 'Active' },
            { value: 'deactivated', label: 'Deactivated' },
            { value: '', label: 'All' },
          ]}
          w={160}
        />
        <Select
          label="Product"
          placeholder="All"
          value={productId || null}
          onChange={(v) => patchParams({ product: v ?? '' }, true)}
          data={[
            { value: '', label: 'All' },
            ...(productsQuery.data ?? []).map((p) => ({
              value: p.id,
              label: p.displayName,
            })),
          ]}
          clearable
          searchable
          w={220}
        />
        <TextInput
          label="Search"
          placeholder="email or name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          w={240}
        />
      </Group>

      <Table highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Email</Table.Th>
            <Table.Th>Display name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Updated</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(query.data?.items ?? []).map((u) => (
            <Table.Tr key={u.id}>
              <Table.Td>{u.email}</Table.Td>
              <Table.Td>{u.display_name}</Table.Td>
              <Table.Td>{userTypeLabel(u.user_type)}</Table.Td>
              <Table.Td>
                <Badge color={u.status === 'active' ? 'green' : 'gray'} variant="light">
                  {u.status}
                </Badge>
              </Table.Td>
              <Table.Td>{new Date(u.updated_at).toLocaleString()}</Table.Td>
              <Table.Td>
                <Anchor component={Link} to={`/users/${u.id}/edit`} size="sm">
                  open
                </Anchor>
              </Table.Td>
            </Table.Tr>
          ))}
          {(query.data?.items ?? []).length === 0 && !query.isPending && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed" ta="center">
                  No users match the current filters.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      <Group justify="space-between">
        <Text c="dimmed" size="sm">
          {total} total
        </Text>
        <Pagination
          value={page}
          total={totalPages}
          onChange={(p) => patchParams({ page: String(p) })}
          size="sm"
        />
      </Group>

      {org && (
        <>
          <Title order={4} mt="md">
            Clinical patients
          </Title>
          <Text c="dimmed" size="sm">
            Patients ingested via case-creation. They have no platform login until promoted.
          </Text>
          {clinicalQuery.isPending ? (
            <SearchLoading />
          ) : (
            <>
              <Table highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>DOB</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Products</Table.Th>
                    <Table.Th>Cases</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {clinicalOnly.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td>
                        {p.given_name} {p.family_name}
                      </Table.Td>
                      <Table.Td>{p.dob}</Table.Td>
                      <Table.Td>{p.email ?? '—'}</Table.Td>
                      <Table.Td>
                        {p.products.length === 0 ? (
                          <Text c="dimmed" size="sm">
                            —
                          </Text>
                        ) : (
                          <Group gap={4} wrap="wrap">
                            {p.products.map((prod) => (
                              <Badge
                                key={prod.id}
                                variant="light"
                                color="blue"
                                title={prod.display_name}
                              >
                                {prod.code}
                              </Badge>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {p.case_count > 0 ? (
                          <Anchor
                            component={Link}
                            size="sm"
                            to={`/cases?patient_id=${encodeURIComponent(p.id)}&org_id=${encodeURIComponent(p.organisation_id)}`}
                          >
                            {p.case_count}
                          </Anchor>
                        ) : (
                          <Text size="sm">{p.case_count}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={p.status === 'active' ? 'green' : 'gray'} variant="light">
                          {p.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-sm"
                          variant="light"
                          onClick={() => {
                            setPromoting(p);
                            openPromote();
                          }}
                        >
                          Promote
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {clinicalOnly.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Text c="dimmed" ta="center">
                          No clinical patients waiting to be promoted.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
              {clinicalQuery.data && clinicalQuery.data.grand_total > 0 && (
                <Group justify="space-between">
                  <Text c="dimmed" size="sm">
                    {debouncedSearch.trim()
                      ? `${clinicalQuery.data.total} matching of ${clinicalQuery.data.grand_total} clinical patients`
                      : `${clinicalQuery.data.total} clinical patients total`}
                  </Text>
                  {clinicalQuery.data.total > 25 && (
                    <Pagination
                      value={page}
                      total={Math.max(1, Math.ceil(clinicalQuery.data.total / 25))}
                      onChange={(p) => patchParams({ page: String(p) })}
                      size="sm"
                    />
                  )}
                </Group>
              )}
            </>
          )}

          <PromoteModal
            opened={promoteOpen}
            patient={promoting}
            onClose={() => {
              closePromote();
              setPromoting(null);
            }}
          />
        </>
      )}
    </Stack>
  );
}

/**
 * Modal that promotes a clinical patient into a platform user. Pre-
 * fills email + display name from the patient row; admin picks the
 * role. Submit posts to the new admin-api endpoint which spans the
 * user-management create + patientAccount link in one call.
 */
function PromoteModal({
  opened,
  patient,
  onClose,
}: {
  opened: boolean;
  patient: ClinicalPatientRow | null;
  onClose: () => void;
}) {
  const promote = usePromoteClinicalPatient();
  const roles = useRolesForOrg(patient?.organisation_id);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal opens on a new patient. Defaults
  // come from the clinical row but stay editable — operators often
  // want to swap a placeholder email for the patient's real address.
  useEffect(() => {
    if (!patient) return;
    setEmail(patient.email ?? '');
    setDisplayName(`${patient.given_name} ${patient.family_name}`.trim());
    setRoleId('');
    setError(null);
  }, [patient?.id]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!patient) return;
    setError(null);
    if (!email.trim() || !displayName.trim() || !roleId) {
      setError('Email, display name, and role are required.');
      return;
    }
    promote.mutate(
      {
        clinical_patient_id: patient.id,
        organisation_id: patient.organisation_id,
        email: email.trim(),
        display_name: displayName.trim(),
        role_id: roleId,
      },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green',
            // See platform-user-create.tsx for the rationale — the
            // wording is intentionally vague so it stays accurate
            // when user-management runs against COGNITO_PROVIDER=mock.
            message: `Promoted ${displayName} — login invitation queued.`,
          });
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setError('A platform user with this email already exists.');
          } else if (err instanceof ApiError && err.status === 400) {
            setError('Validation failed. Check the fields and try again.');
          } else {
            setError('Could not promote patient. Try again.');
          }
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Promote clinical patient" centered>
      {patient && (
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                {error}
              </Alert>
            )}
            <Text size="sm" c="dimmed">
              Creates a platform user linked to{' '}
              <strong>
                {patient.given_name} {patient.family_name}
              </strong>{' '}
              so they can log in. A login invitation is queued — when user-management is configured
              against a real Cognito user pool, the invite email goes out automatically.
            </Text>
            <TextInput
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={promote.isPending}
            />
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={promote.isPending}
            />
            <NativeSelect
              label="Role"
              required
              value={roleId}
              onChange={(e) => setRoleId(e.currentTarget.value)}
              disabled={promote.isPending || (roles.data?.length ?? 0) === 0}
              data={[
                {
                  value: '',
                  label: !roles.data
                    ? 'Loading roles…'
                    : roles.data.length === 0
                      ? 'No roles for this org'
                      : 'Select role…',
                },
                ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
              ]}
            />
            {roles.data && roles.data.length === 0 && (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                This organisation has no roles defined. Create one in{' '}
                <Anchor component={Link} to={`/roles/new?org=${patient.organisation_id}`} size="sm">
                  Roles
                </Anchor>{' '}
                first, then return here to promote.
              </Alert>
            )}
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={onClose} disabled={promote.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={promote.isPending}>
                Promote
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
